// src/app/argent/factureEmission.js — la facture du propriétaire : génération,
// émission, envoi du courriel, relecture des factures.
// CHEMIN ARGENT. Déplacé octet pour octet depuis src/App.jsx (P-3.5, squelette,
// partie 2) : seuls changent l'emballage (signature, retour) et les imports.

import { storage, supabase } from '../../supabase'
import { propDisplayName, fmtE, MONTHS } from '../../lib/format'
import { TEXTE_FACTURE_LECTURE_RATEE } from '../../lib/argent-lecture'
import { makeAppendUpdater } from '../../lib/factlog-save'
import { buildFacturationLignes } from '../../lib/facturation-lignes'
import { getFacturationSource } from '../../lib/facturation-source'
import { genFactureHTML as genFactureHTMLPure, inflateFactureSnapshot, renderDraftPlaceholders } from '../../lib/facture-html'
import { ouvrirApercuIsole } from '../../lib/apercu-isole'
import { resolveInvoicePrefix as resolveInvoicePrefixV2, isDraftInvoice, createDraftInvoice, listInvoices, listInvoicesArchive } from '../../lib/invoices'
import { emettreEtEnvoyerFacture, envoyerFactureExistante, interpreterReponseEnvoi } from '../../lib/facture-send-flow'
// P-2.46 bis : la garde d'écran de l'envoi (mois bloqué pour la session, messages, badge).
import { envoyerFactureMensuelleGardee, signalerFactureDejaExistante, cleMoisFacture, MSG_ENVOI_BROUILLON, MSG_MOIS_A_VERIFIER } from '../../lib/facture-envoi-ecran'
import { montantsConcordent, MODELE_COURRIEL_FACTURE } from '../../lib/courriel-facture'
import { envoiDuJournal, indexerJournalEnvois, JOURNAL_ILLISIBLE } from '../../lib/journal-envois-facture'
import { findBlockingCommission } from '../../lib/invoice-guard'

export function factureEmission({
  cfg, argentLectureRatee, setInvoicesList, setInvoicesArchiveList, journalEnvois, setJournalEnvois,
  envoisBloquesSession, bloquerEnvoiSession, invoicesRaw, setInvoicesRaw, setInvoicesLoaded,
  incidents, month, setConfirm, y, m, monthLabel, setSendingEmail, setGenBusy, setEmailSent,
  factureOpLock, saveLog, dataProjete, ajustFactEmise, facturesMoisBloquees,
}) {
  // Wrapper léger autour de la fonction extraite src/lib/facture-html.js.
  // Permet aux call-sites locaux (sendFactureEmail, doGenFacture) de continuer
  // à appeler genFactureHTML(prop, log, mode) sans avoir à passer toutes les
  // dépendances. La fonction pure extraite est aussi importable depuis
  // scripts/migrate-factlog-snapshot.js (via copie ciblée — voir commit suivant).
  // WAGON M : la facture proprio (chemin argent) est générée sur `dataProjete`
  // pour qu'un logement basculé 'v2' facture les résas Beds24. Court-circuit :
  // tant qu'aucun log n'est 'v2', dataProjete === data (HTML byte-identique).
  // `draft:true` (chantier « numéro à l'envoi ») fige le HTML avec le jeton
  // `__INVOICE_NUMBER__` au lieu d'un numéro : rien n'est consommé côté séquence.
  const genFactureHTML = (prop, log, mode = 'email', invoice_number = null, draft = false) =>
    genFactureHTMLPure({ prop, log, cfg, data: dataProjete, incidents, month, monthLabel, y, m, mode, invoice_number, draft })


  // ── Envoi facture au propriétaire, PDF joint (serveur) ──
  // Phase 3 PR 6 (ADR-066) : numéro séquentiel inviolable via RPC atomique
  // (table invoices_sequences). Plus de cfg.lastNum++ (résout C-1 récupération
  // illégale + C-2 race condition). Persistance dans table invoices V2 +
  // factLog KV (rétrocompat lecture).
  //
  // 2026-09-30 — APPELÉ UNIQUEMENT depuis la fenêtre de relecture du courriel
  // (confirm type `envoiFacture`), au clic de confirmation. `courriel` =
  // { objet, texte } tels que relus/modifiés. L'ordre (validation → numéro →
  // pièce → envoi → preuve) vit dans emettreEtEnvoyerFacture
  // (src/lib/facture-send-flow.js), testé : un courriel invalide ne tire
  // AUCUN numéro.
  //
  // 2026-10-03 (décision Dimitri « PDF joint ») : le courriel part par le
  // serveur (callEnvoyerFactureProprio → api/facture-envoi-proprio.js), qui
  // fabrique le PDF du document archivé, envoie, PUIS note l'envoi au journal. Plus
  // d'EmailJS pour la facture (l'avoir, lui, reste sur EmailJS).
  // `montantRelu` : le montant affiché dans la fenêtre de relecture ; il doit
  // être encore celui de la facture au clic (revue 2026-10-03, point 3).
  const sendFactureEmail = async (prop, log, courriel, montantRelu) => {
    // F13 : données de facturation non lues → montant calculé sur un vide.
    // Aucun numéro P consommé, aucun courriel. (Le bouton est aussi désactivé.)
    if (argentLectureRatee) { console.warn('[facture] envoi refusé : données de facturation non lues'); return }
    if (!prop.email) { alert("Ajoutez l'email de " + propDisplayName(prop) + " dans Params"); return }
    // Verrou synchrone : ferme la fenêtre double-clic (même tick).
    if (factureOpLock.current) return
    factureOpLock.current = true
    setSendingEmail({prop, log})
    try {
      // Commission mensuelle proprio = TOUJOURS émise par FBZ SAS (série 'P').
      // Override LOCAL de l'émetteur ici uniquement (NE PAS toucher resolveEmitter :
      // les factures voyageur proprio doivent rester émetteur proprio — Hoguet).
      const prefix = resolveInvoicePrefixV2({ docType: 'commission_proprio' })

      // Garde anti-doublon au POINT D'ÉCRITURE (table invoices, lignes brutes,
      // + mois bloqués de la session) et blocage du mois dès qu'une pièce a pu
      // être créée, même si la liste ne se relit pas : envoyerFactureMensuelleGardee
      // (src/lib/facture-envoi-ecran.js, testée côté appelant).
      const r = await envoyerFactureMensuelleGardee({
        invoicesRaw, logId: log.id, logNom: log.nom, monthLabel,
        moisBloques: facturesMoisBloquees.current,
        relireListe: refreshInvoices,
        fermer: () => setConfirm(null),
        alerter: (m) => alert(m),
        lancer: () => emettreEtEnvoyerFacture({
        courriel,
        valeurs: { ...variablesCourrielFacture(prop, log), montant: fmtE(montantRelu) },
        formaterMontant: fmtE,
        // P-2.46 : le garde « Assujetti TVA » de l'écran (réponse immédiate) ;
        // celui qui fait foi est le serveur, à l'émission (emettreServeur).
        reglages: cfg,
        // 0. Le montant relu est-il toujours celui qu'on va facturer ? Recalculé
        //    en brouillon (rien n'est tiré ni écrit) et comparé au centime.
        verifierMontant: () => {
          const maintenant = genFactureHTML(prop, log, 'preview', null, true).montantTotal
          return montantsConcordent(montantRelu, maintenant)
            ? { ok: true }
            : { ok: false, raison: `Le montant a changé depuis ta relecture (relu : ${fmtE(montantRelu)}, maintenant : ${fmtE(maintenant)}). Rien n'est parti, aucun numéro tiré : rouvre la fenêtre pour relire le nouveau montant.` }
        },
        // 1. P-2.46 bis : le BROUILLON — aucun numéro tiré par le navigateur.
        //    HTML figé avec les jetons (draft:true, mode 'email' : même document
        //    que l'ancien chemin, jetons mis à part) + amount, en un INSERT.
        creerBrouillon: async () => {
          const {html, montantTotal} = genFactureHTML(prop, log, 'email', null, true)
          // WAGON M (M3.3, F-M3-4) : snapshot STRUCTURÉ des lignes facturées dans
          // meta.lignes (audit/réconciliation, en + du html_snapshot CGI 289). Bâti
          // sur EXACTEMENT la même source que le HTML facture (dataProjete[mk][lid]
          // .reservations, cf. genFactureHTML) → snapshot fidèle au document émis.
          // NB : on n'utilise PAS calc().resas qui inclurait les pseudo-résas
          // pricing_blocks (direct interne) absentes du tableau HTML facture.
          const lignesSnapshot = buildFacturationLignes(dataProjete?.[month]?.[log.id]?.reservations || [], log, month)
          const facturationSource = getFacturationSource(log)
          const inv = await createDraftInvoice({
            prefix,
            invoice_type: 'invoice',
            emitter_type: 'fbz', // override local : émetteur = FBZ SAS pour la commission
            emitter_id: null,
            reservation_id: null,
            logement_id: log.id,
            amount: montantTotal,
            html_snapshot: html, // HTML AVEC jetons — le serveur pose numéro et date
            meta: { mois: monthLabel, prop_nom: prop.nom, log_nom: log.nom, source: 'monthly_proprio', lignes: lignesSnapshot, facturation_source: facturationSource },
          })
          return { inv, html, montantTotal }
        },
        // 2. Le SERVEUR émet : réglages relus en base, garde TVA, puis
        //    assign_invoice_number (même action que « Émettre » un brouillon).
        //    Le document émis (numéro et date posés en base) n'est pas recopié
        //    ici : r.html vaut null, l'Historique l'ouvre par invoiceId.
        emettreServeur: (invoiceId) => callSetInvoiceDelivered(invoiceId, true, { emettreSeulement: true }),
        // 4. Envoi par le SERVEUR, PDF joint, PUIS la note au JOURNAL DES
        //    ENVOIS (à part : la facture émise n'est jamais écrite) — « envoye »
        //    seulement si Resend a accepté le courriel.
        //
        // ⚠️ CE COURRIEL PART RÉELLEMENT en production (MESSAGES_DRY_RUN='false').
        // En simulation (aperçus), le serveur n'envoie rien et le dit.
        destinataireAffiche: prop.email,
        envoyerServeur: ({ invoiceId, objet, texte, destinataireAffiche }) => callEnvoyerFactureProprio(invoiceId, objet, texte, false, destinataireAffiche),
        }),
      })
      // Refus : déjà dit à l'écran par envoyerFactureMensuelleGardee (fenêtre
      // fermée et mois bloqué si une pièce a pu être créée).
      if (!r.ok) return
      const { numero: numFact, inv, html, montantTotal, envoi } = r
      bloquerEnvoiSession(inv.id, envoi)
      // « Parti » = le serveur l'a dit (avec ou sans preuve enregistrée).
      const parti = envoi.etat === 'envoye' || envoi.etat === 'envoye_sans_preuve'

      // 5. factLog KV (rétrocompat lecture). PAS de saveCfg lastNum++ (séquence
      // gérée en DB désormais via RPC).
      // Chantier D2 : entry construite AVANT saveLog (updater pur, StrictMode-safe).
      const entry = {
        num: numFact,
        date: new Date().toLocaleDateString('fr-FR'),
        prop: prop.nom, log: log.nom, logId: log.id, mois: monthLabel,
        // emailEnvoye : SEULEMENT si le serveur a dit « parti » (2026-10-03).
        // Avant, l'entrée disait toujours `true` — une facture refusée ou
        // simulée aurait été lue « Envoyé » (src/lib/facture-statut.js).
        montant: montantTotal, emailEnvoye: parti, emailDest: prop.email,
        htmlSnapshot: html,
        invoiceId: inv.id, // lien vers table invoices V2
      }
      // Read-merge-write sur le KV FRAIS : évite la perte par closure stale
      // (`factLog` capturé pouvait être périmé → last-write-wins effaçait une
      // facture émise entre-temps, ex. P-2026-0026). On relit le blob KV courant.
      // ⚠️ SENS INVERSE d'IcalImportBtn : là-bas `fresh===null` ⇒ abort = sécurité
      // anti-corruption ; ICI abort = re-perte de la facture (déjà en base, source
      // fiscale fiable) → on NE PRÉFIXE JAMAIS l'abort : on merge sur `prev` + log.
      let fresh = null
      try {
        const dr = await storage.get('fbz8-factlog')
        fresh = dr ? JSON.parse(dr.value) : null
      } catch(e){ fresh = null; console.error('[sendFactureEmail/readFreshFactLog]', e) }
      if (fresh === null) console.error('[sendFactureEmail] KV factLog frais indisponible → merge sur état React (anti-perte)')
      await saveLog(makeAppendUpdater(entry, fresh))

      // Re-synchronise invoicesRaw depuis la table → le bouton se grise
      // immédiatement (garde anti-double-facturation à jour sans recharger).
      await refreshInvoices()

      if (parti) setEmailSent(prev => ({...prev, [numFact]: true}))
      if (envoi.etat === 'envoye_sans_preuve') console.error('[sendFactureEmail] courriel parti, preuve refusée :', envoi.message)
      alert(messageApresEnvoi(envoi, numFact, prop.email, { nouvelle: true }))
      setConfirm(null)

    } catch(e) {
      console.error(e)
      // L'envoi lui-même ne lève jamais (callEnvoyerFactureProprio rend un
      // état) : une exception vient du numéro, de la pièce ou du journal. On
      // ne prétend ni « parti » ni « pas parti » : l'Historique dit le vrai
      // (statut lu en base), on le relit.
      alert("Erreur : " + (e?.message || String(e)) + " — relis l'Historique : il montre la facture et son envoi tels qu'ils sont.")
      setConfirm(null)
      refreshInvoices()
    } finally {
      factureOpLock.current = false
      setSendingEmail(null)
    }
  }

  // Ce que l'écran dit après l'envoi — d'après la réponse du SERVEUR seule
  // (interpreterReponseEnvoi, src/lib/facture-send-flow.js, testée).
  // `nouvelle` : la facture vient d'être émise par ce geste (elle existe, même
  // si le courriel n'est pas parti).
  const messageApresEnvoi = (envoi, numero, email, { nouvelle = false } = {}) => {
    const emise = nouvelle ? ` La facture ${numero} est bien émise ; tu pourras l'envoyer depuis l'Historique.` : ''
    if (envoi.etat === 'envoye')
      // Adresse : celle que le SERVEUR a utilisée, masquée (point C).
      return `✅ Facture ${numero} envoyée à ${envoi.destinataire || '—'}, PDF joint. Envoi enregistré.`
    if (envoi.etat === 'envoye_sans_preuve')
      // Le courriel EST parti : dire de NE PAS renvoyer (reçue deux fois sinon).
      return `⚠️ Facture ${numero} : le courriel est bien PARTI à ${envoi.destinataire || '—'}, mais l'envoi n'a PAS pu être noté dans le journal des envois (${envoi.message || 'erreur'}). Ne la renvoie pas : le propriétaire l'a reçue.`
    if (envoi.etat === 'essai')
      return `🧪 Essai de la facture ${numero} envoyé à toi seul (${envoi.destinataire || 'ton adresse'}), objet « [ESSAI] … ». Le propriétaire n'a rien reçu ; rien n'est noté, la facture est inchangée.`
    if (envoi.etat === 'simulation')
      return `ℹ️ Mode simulation : aucun courriel n'est parti, rien n'est enregistré comme envoyé.${emise}`
    if (envoi.etat === 'refuse')
      return `⛔ ${envoi.message}${emise}`
    // incertain : réponse perdue — le courriel est PEUT-ÊTRE parti. « Envoyer »
    // est bloqué pour cette facture (journal, ou repère de session).
    return `⚠️ Facture ${numero} : envoi incertain, le courriel est PEUT-ÊTRE parti. Vérifiez avec le propriétaire avant de renvoyer : « Envoyer » reste bloqué pour cette facture. Après vérification avec le propriétaire, le renvoi passe par l'opérateur.${envoi.message ? ` (${envoi.message})` : ''}`
  }

  // ── Envoyer une facture DÉJÀ émise (2026-09-30) ──
  // Même fenêtre de relecture ; AUCUN numéro tiré, AUCUNE pièce créée : le
  // serveur joint le PDF du document FIGÉ ; l'ordre et le refus vivent dans
  // envoyerFactureExistante (src/lib/facture-send-flow.js).
  const proprioDeLaFacture = (f) => (cfg.props || []).find(p => (p.logs || []).some(l => l.id === f.logId)) || null
  // `essaiSeulement` : fenêtre ouverte depuis « 🧪 Essai » — seul l'essai à
  // l'admin est proposé, jamais « Envoyer » (facture transmise, déjà envoyée…).
  const ouvrirEnvoiFactureExistante = (f, { essaiSeulement = false } = {}) => {
    // F13 : aucun envoi pendant une lecture ratée (même d'un document figé).
    if (argentLectureRatee) { alert(TEXTE_FACTURE_LECTURE_RATEE); return }
    const prop = proprioDeLaFacture(f)
    const log = prop ? (prop.logs || []).find(l => l.id === f.logId) : null
    if (!prop || !log) { alert("Propriétaire introuvable pour ce logement : envoi impossible."); return }
    if (!prop.email) { alert("Ajoutez l'email de " + propDisplayName(prop) + " dans Params"); return }
    setConfirm({ type: 'envoiFacture', existante: f, essaiSeulement: essaiSeulement === true, prop, log, montantApercu: f.montant, objet: MODELE_COURRIEL_FACTURE.objet, texte: MODELE_COURRIEL_FACTURE.texte })
  }
  // `essai` : envoi à l'admin connecté SEUL (destinataire forcé par le serveur),
  // rien n'est noté ; la fenêtre reste ouverte pour envoyer ensuite pour de vrai.
  const envoyerFactureExistanteUI = async (f, prop, log, courriel, { essai = false } = {}) => {
    if (argentLectureRatee) { alert(TEXTE_FACTURE_LECTURE_RATEE); return }
    if (factureOpLock.current) return
    factureOpLock.current = true
    setSendingEmail({ prop, log })
    try {
      const r = await envoyerFactureExistante({
        invoiceId: f.invoiceId,
        numero: f.num,
        courriel,
        valeurs: { ...variablesCourrielFacture(prop, log), mois: f.mois || monthLabel, montant: fmtE(f.montant) },
        essai,
        destinataireAffiche: prop.email,
        envoyerServeur: ({ invoiceId, objet, texte, essai: e, destinataireAffiche }) => callEnvoyerFactureProprio(invoiceId, objet, texte, e, destinataireAffiche),
      })
      if (!r.ok) { alert('⛔ ' + r.raison); return }
      if (!essai) bloquerEnvoiSession(f.invoiceId, r.envoi)
      if (r.envoi.etat === 'envoye_sans_preuve') console.error('[envoyerFactureExistante] courriel parti, preuve refusée :', r.envoi.message)
      alert(messageApresEnvoi(r.envoi, f.num, prop.email))
      if (!essai) setConfirm(null)
    } catch (e) {
      console.error('[envoyerFactureExistante]', e)
      alert("Erreur lors de l'envoi : " + (e?.message || String(e)) + " — relis l'Historique avant de réessayer.")
      setConfirm(null)
    } finally {
      factureOpLock.current = false
      setSendingEmail(null)
      refreshInvoices()
    }
  }

  // Variables du courriel facture (tout sauf numéro et montant, connus au clic).
  const variablesCourrielFacture = (prop, log) => ({
    proprietaire: propDisplayName(prop),
    logement: log.nom,
    mois: monthLabel,
  })

  // Ouvre la fenêtre de relecture du courriel. AUCUN numéro tiré, AUCUNE
  // écriture : le montant affiché vient d'une génération en mode brouillon
  // (jeton à la place du numéro, rien n'est consommé ni enregistré).
  // Message du refus « brouillon » à l'ouverture de la fenêtre d'envoi :
  // MSG_ENVOI_BROUILLON, importé de src/lib/facture-envoi-ecran.js (une seule source).
  const ouvrirEnvoiFacture = (prop, log) => {
    // F13 : le montant de l'aperçu se calculerait sur un vide. (Bouton désactivé aussi.)
    if (argentLectureRatee) { alert(TEXTE_FACTURE_LECTURE_RATEE); return }
    // Une pièce existe déjà pour ce logement/mois (garde partagée avec les
    // ajustements) : refuser TOUT DE SUITE, pas au clic « Envoyer ».
    const bloquante = ajustFactEmise(log)
    if (bloquante) {
      alert(isDraftInvoice(bloquante) ? MSG_ENVOI_BROUILLON : `⛔ Une facture existe déjà pour ${log.nom} — ${monthLabel}.`)
      setConfirm(null)
      return
    }
    // P-2.46 bis : pièce préparée pendant la session, liste peut-être pas relue.
    if (facturesMoisBloquees.current.has(cleMoisFacture(log.id, monthLabel))) { alert(MSG_MOIS_A_VERIFIER); setConfirm(null); return }
    let montantApercu = null
    try {
      montantApercu = genFactureHTML(prop, log, 'preview', null, true).montantTotal
    } catch (e) {
      alert('⛔ ' + (e?.message || e))
      return
    }
    setConfirm({ type: 'envoiFacture', prop, log, montantApercu, objet: MODELE_COURRIEL_FACTURE.objet, texte: MODELE_COURRIEL_FACTURE.texte })
  }

  // Génération facture — ouvre le document dans un nouvel onglet (l'utilisateur
  // imprime via le bouton flottant pour obtenir un PDF natif navigateur).
  //
  // ⚠️ CHANGEMENT DE FOND (chantier « numéro attribué À L'ENVOI », incident
  // 2026-08-04). Ce chemin N'ENVOIE RIEN : il imprime. Il consommait pourtant un
  // numéro de série comme s'il avait transmis la pièce. Supprimer ensuite ce
  // document alors qu'il n'était pas le dernier perdait son numéro pour toujours
  // (resync_invoice_sequence recale sur le MAX PRÉSENT) → TROU dans la série.
  //
  // Désormais il crée un BROUILLON : invoice_number NULL, status='draft', HTML
  // figé avec le jeton `__INVOICE_NUMBER__` et imprimé sous le libellé
  // « BROUILLON ». Aucun numéro n'est consommé → la pièce est supprimable et
  // régénérable autant de fois que voulu sans trouer la numérotation.
  // Le numéro est attribué plus tard, au bouton « Émettre » (Historique ou
  // carte Facturation) — action serveur setInvoiceDelivered en mode
  // emettreSeulement → RPC atomique assign_invoice_number.
  const doGenFacture = async (prop, log) => {
    // F13 : données de facturation non lues → aucun brouillon calculé sur un vide.
    if (argentLectureRatee) { console.warn('[facture] génération refusée : données de facturation non lues'); return }
    // Verrou synchrone : ferme la fenêtre double-clic (même tick).
    if (factureOpLock.current) return
    // Garde anti-doublon au POINT D'ÉCRITURE (table invoices, lignes brutes) :
    // couvre le cas « retour sur l'onglet, re-clic » après refresh.
    if (findBlockingCommission(invoicesRaw, { logId: log.id, monthLabel })) {
      alert(`⛔ Une facture commission existe déjà pour ${log.nom} — ${monthLabel}. Supprimez-la d'abord pour ré-émettre.`)
      setConfirm(null); return
    }
    // P-2.46 bis (équipe rouge) : pièce préparée pendant la session, liste
    // peut-être pas relue (réponse perdue de l'envoi) ⇒ aucun second brouillon.
    if (facturesMoisBloquees.current.has(cleMoisFacture(log.id, monthLabel))) { alert(MSG_MOIS_A_VERIFIER); setConfirm(null); return }
    factureOpLock.current = true
    setGenBusy(true)
    try {
      // Commission mensuelle proprio = TOUJOURS émise par FBZ SAS (série 'P').
      // Override LOCAL de l'émetteur ici uniquement (NE PAS toucher resolveEmitter).
      const prefix = resolveInvoicePrefixV2({ docType: 'commission_proprio' })

      // AUCUN appel à nextInvoiceNumberV2 : rien n'est consommé côté séquence.
      // Le HTML est figé avec le jeton (draft:true) — c'est CE HTML qui part en
      // base ; la substitution du vrai numéro se fera en SQL à l'attribution.
      const {html, montantTotal} = genFactureHTML(prop, log, 'preview', null, true)

      // WAGON M (M3.3, F-M3-4) : snapshot structuré meta.lignes (cf. sendFactureEmail).
      // Même source que le HTML facture (dataProjete[mk][lid].reservations).
      const lignesSnapshot = buildFacturationLignes(dataProjete?.[month]?.[log.id]?.reservations || [], log, month)
      const facturationSource = getFacturationSource(log)

      await createDraftInvoice({
        prefix,
        invoice_type: 'invoice',
        emitter_type: 'fbz', // override local : émetteur = FBZ SAS pour la commission
        emitter_id: null,
        reservation_id: null,
        logement_id: log.id,
        amount: montantTotal,
        html_snapshot: html, // HTML AVEC jeton — ne JAMAIS persister la version rendue
        meta: { mois: monthLabel, prop_nom: prop.nom, log_nom: log.nom, source: 'monthly_proprio_print', lignes: lignesSnapshot, facturation_source: facturationSource },
      })
      // Le brouillon existe : le mois est bloqué pour la session, même si la
      // relecture de la liste ci-dessous échoue (pas de second brouillon).
      facturesMoisBloquees.current.add(cleMoisFacture(log.id, monthLabel))

      // ⚠️ PAS d'entrée factLog KV pour un brouillon : la KV `fbz8-factlog` est un
      // vestige de rétrocompat LECTURE dont toutes les entrées portent un `num`
      // fiscal. Y écrire une entrée sans numéro corromprait les lecteurs legacy
      // (dédup par `num`, tris, exports). La source de vérité est la table
      // `invoices`, que l'Historique lit déjà (refreshInvoices). L'entrée factLog
      // n'a de sens que pour une pièce numérotée.

      // Re-synchronise invoicesRaw depuis la table → le bouton se grise
      // immédiatement (garde anti-double-facturation à jour sans recharger ;
      // findBlockingCommission bloque aussi sur un brouillon, status !== cancelled).
      await refreshInvoices()

      // Impression : jeton → libellé « BROUILLON », puis logo réel injecté.
      // renderDraftPlaceholders s'applique UNIQUEMENT à l'affichage, jamais au snapshot.
      // Faille N1 : document ouvert dans un cadre isolé (src/lib/apercu-isole.js).
      ouvrirApercuIsole(inflateFactureSnapshot(renderDraftPlaceholders(html)))
      setConfirm(null)
    } catch (e) {
      // Lot A3 : la base refuse un second brouillon pour ce logement et ce mois
      // (autre onglet, vieil onglet) — un refus, pas une panne : message clair,
      // mois bloqué pour la session, liste relue, aucun aperçu ouvert.
      if (await signalerFactureDejaExistante({
        e, moisBloques: facturesMoisBloquees.current, logId: log.id, monthLabel,
        relireListe: refreshInvoices, alerter: (msg) => alert(msg), fermer: () => setConfirm(null),
      })) return
      console.error('[doGenFacture] échec :', e)
      // ── FILET DE TRANSITION (code déployé AVANT la migration 20261115000001) ──
      // Tant que `invoices.invoice_number` est encore NOT NULL en base, l'INSERT
      // d'un brouillon (invoice_number: null) échoue en 23502 not_null_violation.
      // Sans ce message, l'admin voit « Erreur génération facture : null value in
      // column … » et croit à une panne : c'est juste la migration qui manque.
      // Pendant t : le bouton est indisponible, AUCUN numéro n'est consommé et
      // aucune ligne n'est créée — état sûr, simplement bloqué.
      // Miroir du 409 assign_rpc_unavailable côté « envoyé » (invoice-handlers.js).
      // ⚠️ À RETIRER une fois 20261115000001 appliquée et vérifiée.
      const code = String(e?.code || '')
      const msg = String(e?.message || '')
      if (code === '23502' || /not[- ]null|null value in column/i.test(msg)) {
        alert(
          "Les brouillons de facture ne sont pas encore actifs en base (migration non appliquée).\n\n" +
          "Aucun numéro n'a été consommé et aucune facture n'a été créée. Réessaie une fois la migration appliquée.",
        )
      } else {
        alert("Erreur génération facture : " + (e?.message || JSON.stringify(e)))
      }
    } finally {
      factureOpLock.current = false
      setGenBusy(false)
    }
  }

  // ── Suppression d'un BROUILLON (chantier Pass A, Point 1d) ──
  // Source fiscale = table invoices (PAS le factLog KV). Le serveur
  // (deleteInvoiceHard, service_role) ne supprime qu'un brouillon sans numéro ;
  // une facture émise est refusée (409) : on l'annule par un avoir. Réponse
  // → { ok, message } (json.message || json.error || http_<status>).
  const callDeleteInvoice = async (invoiceId) => {
    const { data: { session } } = await supabase.auth.getSession()
    const body = { action: 'deleteInvoiceHard', invoiceId }
    const res = await fetch('/api/beds24-mapping?action=deleteInvoiceHard', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session?.access_token || ''}`,
      },
      body: JSON.stringify(body),
    })
    const json = await res.json().catch(() => ({}))
    return {
      ok: res.ok,
      message: json.message || json.error || `Suppression refusée (http_${res.status}).`,
    }
  }

  // ── Historique factures (source = table invoices) ──────────────────────
  // Mappe une ligne table → shape attendue par HistoriquePanel (ex-factLog KV).
  // `journal` : index du journal des envois ; par défaut l'état (rendu), mais
  // refreshInvoices passe celui qu'il vient de lire (l'état n'est pas encore à jour).
  const mapInvoiceRow = (r, journal = journalEnvois) => {
    const d = (r.sent_at || r.created_at) ? new Date(r.sent_at || r.created_at) : null
    // Fallback `mois` depuis la date d'émission si meta.mois absent (factures
    // voyageur FactureTab) → évite le groupe « ? » + l'invisibilité au filtre année.
    const mois = (r.meta && r.meta.mois) || (d ? `${MONTHS[d.getMonth()]} ${d.getFullYear()}` : '')
    // BROUILLON (chantier « numéro à l'envoi ») : invoice_number est NULL tant que
    // rien n'a été envoyé. On propage la colonne BRUTE `invoice_number` : c'est
    // elle que lisent isDraftInvoice() / invoiceDisplayNumber() (src/lib/invoices.js,
    // source unique de la règle). Aucune règle « est-ce un brouillon » n'est
    // réécrite ici ni chez les consommateurs.
    //
    // ⚠️ `num` PORTE LE NUMÉRO RÉEL, null pour un brouillon. Une version
    // antérieure y mettait un `brouillon-<uuid>` de repli « jamais affiché » —
    // c'était FAUX : la carte de l'onglet Facturation l'affichait brut
    // (« ✓ Facturé · N° brouillon-3f2a1c8e… ») et la modale de suppression aussi.
    // La clé React vit désormais dans `rowKey`, jamais dans `num` : aucun champ
    // de cette shape ne peut plus être pris pour un numéro de série.
    //
    // `date` = date d'ÉMISSION. Un brouillon n'est pas émis : elle reste vide
    // (created_at n'est pas une date d'émission — cf. option A, la date est posée
    // par assign_invoice_number en même temps que le numéro).
    const isDraft = isDraftInvoice(r)
    return {
      num: r.invoice_number,
      invoice_number: r.invoice_number,
      rowKey: r.id,
      mois,
      prop: (r.meta && r.meta.prop_nom) || (r.meta && r.meta.voyageur) || '',
      log: (r.meta && r.meta.log_nom) || '',
      logId: r.logement_id || '',
      montant: Number(r.amount) || 0,
      date: isDraft ? '' : (d ? d.toLocaleDateString('fr-FR') : ''),
      // `emailEnvoye` = delivered_at posé (« transmise ») : c'est ce que la
      // garde serveur de suppression regarde. Ce N'EST PAS une preuve de
      // courriel (pastille manuelle, 29 pièces sur 29) — l'écran dit
      // « Envoyé » d'après `envoiCourriel` (src/lib/facture-statut.js).
      emailEnvoye: !!r.delivered_at,
      // « Envoyé » : la note du JOURNAL DES ENVOIS (décision Dimitri 2026-10-03,
      // la facture émise n'est jamais écrite) ; journal illisible → envoiInconnu.
      ...envoiDuJournal(journal, r.id, envoisBloquesSession.current),
      // Ancien envoi (séries 2026-19 à 25) : meta.email_envoye=true = preuve.
      emailEnvoyeMeta: !!(r.meta && r.meta.email_envoye === true),
      // Facture émise HORS application (P-2026-0048→0055, panne août-sept) :
      // son html_snapshot n'est qu'une fiche de renvoi — jamais « Envoyer ».
      horsApplication: !!(r.meta && r.meta.hors_application),
      invoiceId: r.id,
      emailDest: (r.meta && r.meta.email_dest) || null,
      // Série de la pièce (P, V, AVE…) : nécessaire pour annoncer la série de
      // l'avoir (AV-{prefix}) AVANT le clic. Statut + lien pour l'archive.
      prefix: r.prefix || null,
      status: r.status || null,
      relatedInvoiceId: r.related_invoice_id || null,
      originalNumber: (r.meta && r.meta.original_number) || null,
      // BUG 37 — type de document (receipt / invoice) propagé à l'historique
      // pour distinguer « Reçu » de « Facture » sans agréger sous un libellé unique.
      kind: r.invoice_type,
    }
  }
  // Relecture tour 3 (point 5) — import CSV : les factures sont RELUES au
  // moment de « Confirmer » (onglet resté ouvert = factures périmées). Rend
  // les lignes brutes ; une erreur REMONTE (l'import est alors refusé).
  const relireFacturesPourImport = async () => {
    const rows = await listInvoices()
    setInvoicesList(rows.map(mapInvoiceRow))
    setInvoicesRaw(rows)
    setInvoicesLoaded(true)
    return rows
  }
  const refreshInvoices = async () => {
    // Le journal d'abord : les lignes se construisent avec lui.
    const journal = await refreshJournalEnvois()
    try {
      const rows = await listInvoices()
      setInvoicesList(rows.map(r => mapInvoiceRow(r, journal)))
      // Conserve aussi les lignes BRUTES pour la garde anti-double-facturation.
      setInvoicesRaw(rows)
      setInvoicesLoaded(true)
    } catch (e) {
      console.error('[historique] listInvoices', e)
    }
    // Archive (annulées + avoirs) : lecture séparée, jamais mêlée à invoicesRaw
    // (la garde et le « Total émis » ne comptent que les pièces actives). Un
    // échec ici ne doit pas priver l'écran des pièces actives.
    try {
      const archive = await listInvoicesArchive()
      setInvoicesArchiveList(archive.map(r => mapInvoiceRow(r, journal)))
    } catch (e) {
      console.error('[historique] listInvoicesArchive', e)
    }
  }
  // Le journal des envois, lu par le serveur (GET /api/facture-envoi-proprio).
  // Lecture ratée → JOURNAL_ILLISIBLE : l'écran dit « Envoi : — ».
  const refreshJournalEnvois = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch('/api/facture-envoi-proprio', {
        method: 'GET',
        headers: { Authorization: `Bearer ${session?.access_token || ''}` },
      })
      const json = await res.json()
      if (!res.ok || !json || json.ok !== true || !Array.isArray(json.envois)) throw new Error(`journal_illisible (http_${res.status})`)
      const index = indexerJournalEnvois(json.envois)
      setJournalEnvois(index)
      return index
    } catch (e) {
      console.error('[historique] journal des envois', e)
      setJournalEnvois(JOURNAL_ILLISIBLE)
      return JOURNAL_ILLISIBLE
    }
  }
  // Action serveur setInvoiceDelivered : attribue le numéro d'un brouillon et/ou
  // pose la date de transmission. Appelée par « Émettre » et après un envoi.
  // opts (2026-09-30, lib/invoice-handlers.js) — n'entrent dans le corps QUE
  // s'ils valent `true` strict :
  //   · emettreSeulement : bouton « Émettre » d'un brouillon (numéro, sans
  //     delivered_at) ;
  const callSetInvoiceDelivered = async (invoiceId, delivered, opts = {}) => {
    const { data: { session } } = await supabase.auth.getSession()
    const body = { action: 'setInvoiceDelivered', invoiceId, delivered }
    if (opts && opts.emettreSeulement === true) body.emettreSeulement = true
    const res = await fetch('/api/beds24-mapping?action=setInvoiceDelivered', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session?.access_token || ''}`,
      },
      body: JSON.stringify(body),
    })
    const json = await res.json().catch(() => ({}))
    // `...json` propage invoice_number / number_assigned (attribution du numéro
    // d'un brouillon) sans changer le contrat { ok, message } historique.
    // `status` (P-2.46 bis) : sépare un vrai refus (4xx nommé) d'une réponse
    // perdue (5xx) — lireRefusEmission, src/lib/facture-send-flow.js.
    return { ...json, ok: res.ok, status: res.status, message: json.message || json.error || `Échec (http_${res.status}).` }
  }
  // Envoi d'une facture au propriétaire, PDF joint (2026-10-03) — par le
  // serveur (api/facture-envoi-proprio.js). Ne LÈVE JAMAIS : rend l'état lu
  // dans la réponse (interpreterReponseEnvoi). Une réponse perdue = « incertain »,
  // jamais « non envoyé ». Le destinataire est lu par le serveur dans la fiche.
  // `destinataireAffiche` : l'adresse montrée dans la fenêtre — le serveur
  // refuse si la fiche dit autre chose (point C, revue 2026-10-03).
  const callEnvoyerFactureProprio = async (invoiceId, objet, texte, essai = false, destinataireAffiche = null) => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch('/api/facture-envoi-proprio', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token || ''}`,
        },
        body: JSON.stringify(essai === true ? { invoice_id: invoiceId, objet, texte, essai: true } : { invoice_id: invoiceId, objet, texte, destinataire_affiche: destinataireAffiche }),
      })
      let json = null
      try { json = await res.json() } catch (e) { console.error('[callEnvoyerFactureProprio] réponse illisible', e) }
      return interpreterReponseEnvoi({ httpOk: res.ok, json })
    } catch (e) {
      console.error('[callEnvoyerFactureProprio]', e)
      return interpreterReponseEnvoi({ erreurReseau: e })
    }
  }
  return {
    sendFactureEmail, ouvrirEnvoiFactureExistante, envoyerFactureExistanteUI,
    variablesCourrielFacture, ouvrirEnvoiFacture, doGenFacture, callDeleteInvoice, mapInvoiceRow,
    relireFacturesPourImport, refreshInvoices, callSetInvoiceDelivered,
  }
}
